import { AxiosError } from 'axios'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { Nullable } from '@shared/models/types/primary'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import { useUserLocale } from '@shared/utils/composables/locales'
import { createSharedComposable } from '@shared/utils/helpers/create-composable'
import {
  getErrorByCode,
  getErrorMessages,
} from '@shared/utils/helpers/errors/error-handling'
import { displayErrorMessages } from '@shared/utils/helpers/errors/error-message'
import { lsGet, LsGlobalKeys } from '@shared/utils/helpers/local-storage'
import { isPending } from '@shared/utils/helpers/request-status'

import { ApiErrorCode } from '@protos/api_error_code_pb'
import { ApiErrorResponse } from '@protos/response/api_error_response_pb'

import i18n from '@pc/i18n'
import { ICreateDataConversation } from '@pc/models/proto/base/conversations'
import router from '@pc/router'
import routeNames from '@pc/router/route-names'
import { useChatWidgetsStore } from '@pc/store/chat-widgets'
import { useConversationsStore } from '@pc/store/conversations'
import { useSessionStore } from '@pc/store/session'
import type { JsonValue } from '@protobuf-ts/runtime'

export const conversationErrorsHandler = (
  error: AxiosError,
  withRedirect = true
) => {
  const { t } = i18n.global

  const { response } = error
  const { errors } = ApiErrorResponse.fromJson(response?.data as JsonValue)
  const messages = getErrorMessages(errors)

  const widgetUnavailableError = getErrorByCode(
    errors,
    ApiErrorCode.PC_CHAT_WIDGET_UNAVAILABLE
  )

  const disallowedByTimeLapseError = getErrorByCode(
    errors,
    ApiErrorCode.PC_NEW_CONVERSATION_CREATING_DISALLOWED_BY_TIME_LAPSE
  )

  if (widgetUnavailableError) {
    const replaceData = {
      name: routeNames.widgetUnavailable,
    }
    if (withRedirect) {
      router.replace({
        name: replaceData.name,
      })
    }
    return replaceData as Route
  } else if (disallowedByTimeLapseError) {
    const redirectData = previousConversationData()

    if (redirectData) {
      if (withRedirect) {
        router.push({
          name: redirectData.name,
          params: { ...redirectData.params },
          query: redirectData.query || {},
        })
      }
      return redirectData as Route
    }
  } else {
    displayErrorMessages(messages, t('alerts.errors.unexpected'))
  }
}

export const previousConversationData = () => {
  const { chatProps } = useSessionStore()
  const conversationsStore = useConversationsStore()
  const route = useRoute()
  const isPatient = chatProps?.visitant.case === 'patient'
  const patientConversationId =
    lsGet(LsGlobalKeys.CurrentConversationId) ??
    chatProps?.lastOpenConversationId
  const visitorConversationId =
    chatProps?.lastOpenConversationId ??
    conversationsStore.allConversations[0]?.id

  const conversationId = isPatient
    ? patientConversationId
    : visitorConversationId

  if (conversationId) {
    return {
      name: routeNames.conversation,
      params: { conversationId },
      query: route?.query || {},
    } as Route
  }
  return null
}

export const useCreatePatientConversation = createSharedComposable(() => {
  const sessionStore = useSessionStore()
  const conversationsStore = useConversationsStore()
  const { userLocale } = useUserLocale()
  const { fetchData, isLoading } = useFetchData()
  const chatWidgetsStore = useChatWidgetsStore()

  const route = useRoute()
  const router = useRouter()

  const canStartNewConversation = computed(() => {
    return (
      chatWidgetsStore.widget.data?.canStartNewConversation &&
      !isPending(chatWidgetsStore.widget)
    )
  })

  const kioskModeLandingUrl = computed(
    () => chatWidgetsStore.widget.data?.kioskModeLandingUrl
  )

  const createConversationParams = (): ICreateDataConversation => {
    return {
      chatWidgetId: sessionStore.chatProps?.chatWidget?.id ?? 0,
      locale: userLocale.value || 'en',
    }
  }

  const updatePatientConversation = (conversationId: Nullable<number>) => {
    if (!conversationId) {
      return
    }

    fetchData({
      onFetchDataCallback: async () => {
        await conversationsStore.escalateToAgentConversation({ conversationId })
        router.push({
          name: routeNames.conversation,
          params: { conversationId },
          query: route?.query || {},
        })
      },
      onErrorCatchCallback: (error) =>
        conversationErrorsHandler(error as AxiosError),
    })
  }

  const startOverConversation = () => {
    window.location.replace(
      `${kioskModeLandingUrl.value}&visitor_source=website_url&windowscale`
    )
  }

  const createPatientConversation = (isHuman = false) => {
    fetchData({
      onFetchDataCallback: async () => {
        const { data } = await conversationsStore.createConversation(
          createConversationParams()
        )
        const query = isHuman
          ? { ...route.query, isHuman: String(isHuman) }
          : { ...route.query }

        router.push({
          name: routeNames.conversation,
          params: {
            conversationId: data?.id ?? 0,
          },
          query,
        })
      },
      onErrorCatchCallback(error) {
        conversationErrorsHandler(error as AxiosError)
      },
    })
  }

  const fetchWidgetData = () => {
    chatWidgetsStore.getOrFetchWidgetById({
      chatWidgetId: sessionStore.chatProps?.chatWidget?.id ?? 0,
    })
  }

  fetchWidgetData()

  return {
    loading: isLoading,
    createPatientConversation,
    startOverConversation,
    updatePatientConversation,
    canStartNewConversation,
  }
})

type Route = {
  name?: string
  query?: Record<string, string | number | null | string[]>
  params: Record<string, string | number | null | string[]>
}

export const createConversation = async (
  noRedirects?: boolean,
  locale?: string
) => {
  const conversationsStore = useConversationsStore()
  const { chatProps } = useSessionStore()

  try {
    const payload: ICreateDataConversation = {
      chatWidgetId: chatProps?.chatWidget?.id ?? 0,
      locale: locale || 'en',
    }
    const resp = await conversationsStore.createConversation(payload)
    if (resp) {
      return {
        params: {
          conversationId: resp.data?.id ?? 0,
        },
      } as Route
    }
  } catch (error) {
    return conversationErrorsHandler(error as AxiosError, !noRedirects) as Route
  }
}
